import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  REMITTANCE_TOKENS,
  SAMPLE_REM_CONTEXT,
  buildTemplateFromSegments,
  parseTemplateToSegments,
  renderRemittanceTemplate,
} from "../../../../components/sepa/remittanceTokens";
import RemittanceSegmentChip from "./RemittanceSegmentChip";

export default function RemittanceBuilderDialog({
  open,
  initialTemplate,
  onClose,
  onSave,
  loading = false,
}) {
  const [segments, setSegments] = useState([]);
  const [customText, setCustomText] = useState("");

  useEffect(() => {
    if (!open) return;
    setSegments(parseTemplateToSegments(initialTemplate || ""));
    setCustomText("");
  }, [open, initialTemplate]);

  const addToken = (key) => {
    setSegments((prev) => [...prev, { type: "token", key }]);
  };

  const addText = () => {
    if (!customText.trim()) return;
    setSegments((prev) => [...prev, { type: "text", value: customText }]);
    setCustomText("");
  };

  const handleDeleteSegment = (index) => {
    setSegments((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      addText();
    }
  };

  const templateValue = useMemo(
    () => buildTemplateFromSegments(segments),
    [segments]
  );

  const previewValue = useMemo(
    () => renderRemittanceTemplate(templateValue, SAMPLE_REM_CONTEXT),
    [templateValue]
  );

  const previewLength = previewValue.length;
  const exceedsLimit = previewLength > 140;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Verwendungszweck konfigurieren</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Stellen Sie den Verwendungszweck aus Platzhaltern und eigenem Text
          zusammen. Die finale Länge wird automatisch auf 140 Zeichen
          begrenzt.
        </Typography>

        <Stack spacing={2}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Aktuelle Reihenfolge
            </Typography>
            {segments.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Noch keine Elemente hinzugefügt.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                {segments.map((segment, index) => (
                  <RemittanceSegmentChip
                    key={`${segment.type}-${index}`}
                    segment={segment}
                    onDelete={() => handleDeleteSegment(index)}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Eigenen Text hinzufügen
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Text"
                value={customText}
                onChange={(event) => setCustomText(event.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Button variant="outlined" onClick={addText}>
                Hinzufügen
              </Button>
              <Button
                variant="text"
                color="secondary"
                onClick={() => setSegments([])}
              >
                Zurücksetzen
              </Button>
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Platzhalter
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {REMITTANCE_TOKENS.map((token) => (
                <Tooltip
                  key={token.key}
                  title={token.description || token.sample}
                  arrow
                >
                  <Chip
                    variant="outlined"
                    label={token.label}
                    onClick={() => addToken(token.key)}
                  />
                </Tooltip>
              ))}
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Vorschau (Beispieldaten)
            </Typography>
            <Typography
              variant="body2"
              sx={{
                p: 2,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: (theme) => theme.palette.action.hover,
                whiteSpace: "pre-wrap",
              }}
            >
              {previewValue || "(Leer)"}
            </Typography>
            <Typography
              variant="caption"
              color={exceedsLimit ? "error" : "text.secondary"}
            >
              Vorschau-Länge: {previewLength} Zeichen
              {exceedsLimit ? " – der finale Text wird gekürzt." : ""}
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button
          variant="contained"
          onClick={() => onSave(templateValue)}
          disabled={loading}
        >
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RemittanceBuilderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  initialTemplate: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

